import {useEffect, useState, useRef} from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {useNavigate} from "react-router-dom";
import {Card, Divider, InputLabel, TextField, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET, POST} from "APIclient";
import properties from "constants/ApplicationConstants";

function AddTechnicalSkills() {
  const navigate = useNavigate();
  const [empId, setEmpId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({open: false, message: "", type: ""});

  const [primarySkills, setPrimarySkills] = useState([]);
  const [secondarySkills, setSecondarySkills] = useState([]);
  const [technicalSecondarySkills, setTechnicalSecondarySkills] = useState([]);
  const [existingTechnicalPrimarySkills, setExistingTechnicalPrimarySkills] = useState([]);
  const [newTechnicalPrimarySkills, setNewTechnicalPrimarySkills] = useState([
    {id: 1, value: "", isNew: true}
  ]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false, message: "", skillsToAdd: [], duplicateInfo: []
  });

  const inputRefs = useRef({});

  useEffect(() => {
    const storedEmpId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
    if (storedEmpId) {
      setEmpId(parseInt(storedEmpId));
      fetchExistingSkills(parseInt(storedEmpId));
    } else {
      setLoading(false);
      showNotification("Employee ID not found", "error");
    }
  }, []);

  const fetchExistingSkills = async employeeId => {
    try {
      setLoading(true);
      const response = await GET(`manager/list-kraSkills/${employeeId}`);
      if (response.data?.data) {
        const skillsData = response.data.data;
        const primarySkillsList = [], secondarySkillsList = [], technicalSecondarySkillsList = [], technicalPrimarySkillsList = [];

        skillsData.forEach((skill, index) => {
          if (skill.status !== 1) return;
          const skillObj = {id: index + 1, value: skill.skillSet, paFormId: skill.paFormId, isNew: false};
          const skillType = skill.skillTypes.toLowerCase().trim().replace(/[-\s]/g, "");
          if (skillType === "competencyprimary" || skillType === "performancecompetencyprimary") primarySkillsList.push(skillObj);
          else if (skillType === "competencysecondary" || skillType === "performancecompetencysecondary") secondarySkillsList.push(skillObj);
          else if (skillType === "technicalsecondary") technicalSecondarySkillsList.push(skillObj);
          else if (skillType === "technicalprimary") technicalPrimarySkillsList.push(skillObj);
        });

        setPrimarySkills(primarySkillsList);
        setSecondarySkills(secondarySkillsList);
        setTechnicalSecondarySkills(technicalSecondarySkillsList);
        setExistingTechnicalPrimarySkills(technicalPrimarySkillsList);
      }
    } catch (error) {
      showNotification("Failed to load existing skills", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({open: true, message, type});
    setTimeout(() => setNotification({open: false, message: "", type: ""}), 4000);
  };

  const isValidSkillText = text => {
    if (!text || text.trim() === "") return false;
    const trimmedText = text.trim();
    if (!/[a-zA-Z]/.test(trimmedText)) return false;
    if (/^[^a-zA-Z0-9]+$/.test(trimmedText)) return false;
    return true;
  };

  const getCategoryName = category => ({
    primarySkills: "Performance Competency Primary Skill Set",
    secondarySkills: "Performance Competency Secondary Skill Set",
    technicalSecondarySkills: "Technical Secondary Skill",
    existingTechnicalPrimarySkills: "Technical Primary Skill (Existing)"
  }[category] || category);

  const findSimilarSkills = newSkillValue => {
    const normalizedNewSkill = newSkillValue.toLowerCase().trim();
    const matches = [];
    const extractWords = text => text.toLowerCase().trim().split(/[\s\-_.,;:!?()[\]{}]+/).filter(w => w.length > 0);
    const areSimilar = (s1, s2) => {
      if (s1.toLowerCase().trim() === s2.toLowerCase().trim()) return true;
      const words1 = extractWords(s1), words2 = extractWords(s2);
      for (const w1 of words1) for (const w2 of words2)
        if ((w1.includes(w2) || w2.includes(w1)) && (w1.length >= 3 || w2.length >= 3)) return true;
      return false;
    };
    [{name: "primarySkills", skills: primarySkills}, {name: "secondarySkills", skills: secondarySkills},
     {name: "technicalSecondarySkills", skills: technicalSecondarySkills}, {name: "existingTechnicalPrimarySkills", skills: existingTechnicalPrimarySkills}]
      .forEach(cat => cat.skills.forEach(skill => {
        if (areSimilar(skill.value, normalizedNewSkill))
          matches.push({category: cat.name, categoryDisplay: getCategoryName(cat.name), existingSkill: skill.value, newSkill: newSkillValue});
      }));
    return matches;
  };

  const handleAddSkill = () => {
    if (newTechnicalPrimarySkills.some(s => !isValidSkillText(s.value))) {
      showNotification("Please enter valid text in existing fields before adding more", "warning");
      return;
    }
    const newId = Math.max(...newTechnicalPrimarySkills.map(s => s.id), 0) + 1;
    setNewTechnicalPrimarySkills([...newTechnicalPrimarySkills, {id: newId, value: "", isNew: true}]);
    setTimeout(() => inputRefs.current[newId]?.focus(), 100);
  };

  const handleSkillChange = (id, value) =>
    setNewTechnicalPrimarySkills(newTechnicalPrimarySkills.map(s => s.id === id ? {...s, value} : s));

  const handleRemoveSkill = id => {
    if (newTechnicalPrimarySkills.length > 1)
      setNewTechnicalPrimarySkills(newTechnicalPrimarySkills.filter(s => s.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (newTechnicalPrimarySkills.some(s => !isValidSkillText(s.value))) {
        showNotification("Please enter valid text in all fields", "warning"); setSaving(false); return;
      }
      const filteredSkills = newTechnicalPrimarySkills.filter(s => s.value.trim() !== "").map(s => s.value.trim());
      if (filteredSkills.length === 0) { showNotification("Please add at least one technical primary skill", "warning"); setSaving(false); return; }

      const exactDuplicates = [], nonExact = [];
      const allCats = [
        {name: "Performance Competency Primary Skill Set", skills: primarySkills},
        {name: "Performance Competency Secondary Skill Set", skills: secondarySkills},
        {name: "Technical Secondary Skill", skills: technicalSecondarySkills},
        {name: "Technical Primary Skill (Existing)", skills: existingTechnicalPrimarySkills}
      ];
      filteredSkills.forEach(sv => {
        const norm = sv.toLowerCase().trim();
        let found = false;
        allCats.forEach(cat => cat.skills.forEach(skill => {
          if (skill.value.toLowerCase().trim() === norm) { exactDuplicates.push({skill: sv, category: cat.name, existingSkill: skill.value}); found = true; }
        }));
        if (!found) nonExact.push(sv);
      });

      if (exactDuplicates.length > 0) {
        showNotification("The following skill(s) already exist:\n\n" + exactDuplicates.map(i => `• "${i.skill}" in ${i.category}`).join("\n"), "error");
        setSaving(false); return;
      }
      if (nonExact.length === 0) { showNotification("All skills already exist.", "error"); setSaving(false); return; }

      const allSimilar = nonExact.map(sv => ({newSkill: sv, matches: findSimilarSkills(sv)})).filter(i => i.matches.length > 0);
      if (allSimilar.length > 0) {
        setConfirmDialog({open: true, message: "", skillsToAdd: nonExact, duplicateInfo: allSimilar});
        setSaving(false); return;
      }
      await proceedWithSave(nonExact);
    } catch (error) {
      showNotification(error.response?.data?.message || "An error occurred while saving skills.", "error");
      setSaving(false);
    }
  };

  const proceedWithSave = async skillsToAdd => {
    setSaving(true);
    try {
      const response = await POST("manager/add-kraSkills", {empId, skills: skillsToAdd, skillType: "technical-primary"});
      if (response.data && response.status === 200) {
        showNotification("Skills added successfully!", "success");
        setTimeout(() => { fetchExistingSkills(empId); setNewTechnicalPrimarySkills([{id: 1, value: "", isNew: true}]); }, 1000);
      } else {
        showNotification("Failed to add skills. Please try again.", "error");
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "An error occurred while saving skills.", "error");
    } finally {
      setSaving(false);
      setConfirmDialog({open: false, message: "", skillsToAdd: [], duplicateInfo: []});
    }
  };

  const handleConfirmDialogClose = confirmed => {
    if (confirmed) proceedWithSave(confirmDialog.skillsToAdd);
    else { setConfirmDialog({open: false, message: "", skillsToAdd: [], duplicateInfo: []}); setSaving(false); }
  };

  // Shared input sx
  const fieldSx = {
    "& .MuiInputBase-input": {padding: "5px 8px", fontSize: "0.82rem"},
    "& .MuiInputBase-input.Mui-disabled": {WebkitTextFillColor: "#000", backgroundColor: "#f5f5f5"},
    "& .MuiFormHelperText-root": {margin: "2px 0 0 0", fontSize: "0.68rem"},
  };

  const renderReadOnlySkillSection = (title, skills) => {
    if (!skills.length) return null;
    return (
      <MDBox mb={1}>
        <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5} sx={{fontSize: "0.82rem"}}>
          {title}
        </MDTypography>
        <MDBox display="flex" flexWrap="wrap" gap={0.75}>
          {skills.map((skill, index) => (
            <MDBox key={skill.id} display="flex" alignItems="center" gap={0.4}>
              <InputLabel sx={{minWidth: "22px", fontSize: "0.78rem", color: "#555"}}>{index + 1}.</InputLabel>
              <TextField
                size="small"
                value={skill.value}
                variant="outlined"
                disabled
                sx={{...fieldSx, minWidth: "180px", "& .MuiInputBase-root": {width: "fit-content"}}}
              />
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    );
  };

  const renderNewSkillSection = (title, skills) => (
    <MDBox mb={0.75}>
      <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5} sx={{fontSize: "0.82rem"}}>
        {title}
      </MDTypography>
      <MDBox display="flex" flexWrap="wrap" gap={0.75}>
        {skills.map((skill, index) => {
          const isInvalid = skill.value && !isValidSkillText(skill.value);
          return (
            <MDBox key={skill.id} display="flex" alignItems="center" gap={0.4}>
              <InputLabel sx={{minWidth: "22px", fontSize: "0.78rem", color: "#555"}}>
                {existingTechnicalPrimarySkills.length + index + 1}.
              </InputLabel>
              <TextField
                size="small"
                value={skill.value}
                onChange={e => handleSkillChange(skill.id, e.target.value)}
                placeholder="Enter technical primary skill"
                variant="outlined"
                inputRef={el => inputRefs.current[skill.id] = el}
                error={isInvalid}
                helperText={isInvalid ? "Please enter valid text with at least one letter" : ""}
                sx={{...fieldSx, minWidth: "220px"}}
              />
              {skills.length > 1 && (
                <MDButton variant="text" color="error" onClick={() => handleRemoveSkill(skill.id)} size="small" sx={{minWidth: "auto", p: 0.25, fontSize: "0.75rem"}}>
                  ✕
                </MDButton>
              )}
            </MDBox>
          );
        })}
      </MDBox>
      <MDBox mt={0.75} display="flex" alignItems="center" gap={1.5}>
        <MDButton variant="outlined" color="info" onClick={handleAddSkill} size="small" sx={{fontSize: "0.75rem", py: 0.3, px: 1}}>
          + Add More
        </MDButton>
        <MDTypography variant="caption" sx={{fontStyle: "italic", fontWeight: 600, color: "#e65100", fontSize: "0.72rem"}}>
          ⚠️ Below skills are static and kindly do not repeat them under Technical Primary skills.
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1.5}>
        <Card>
          <MDBox p={1}>
            <MDTypography variant="h6" fontWeight="600" mb={0.75} sx={{fontFamily: properties.fontFamily, fontSize: "0.95rem"}}>
              Add Skills
            </MDTypography>

            {/* Technical Primary Section */}
            <MDBox sx={{border: "1.5px solid", borderColor: "grey.300", borderRadius: "8px", p: 1, mb: 1}}>
              {renderReadOnlySkillSection("Technical Primary Skill (Existing)", existingTechnicalPrimarySkills)}
              {existingTechnicalPrimarySkills.length > 0 && <Divider sx={{my: 0.75}} />}
              {renderNewSkillSection("Add New Technical Primary Skills", newTechnicalPrimarySkills)}

              <MDBox display="flex" justifyContent="flex-end" gap={1} mt={1}>
                <MDButton variant="outlined" color="dark" onClick={() => navigate(-1)} disabled={saving} size="small">
                  Back
                </MDButton>
                <MDButton variant="gradient" color="info" onClick={handleSave} disabled={saving} size="small" sx={{fontFamily: properties.fontFamily}}>
                  {saving ? "Saving..." : "Save"}
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Technical Secondary Skill */}
            {renderReadOnlySkillSection("Technical Secondary Skill", technicalSecondarySkills)}
            {technicalSecondarySkills.length > 0 && <Divider sx={{my: 0.75}} />}

            {/* Performance Competency Primary */}
            {renderReadOnlySkillSection("Performance Competency Primary Skill Set", primarySkills)}
            {primarySkills.length > 0 && <Divider sx={{my: 0.75}} />}

            {/* Performance Competency Secondary */}
            {renderReadOnlySkillSection("Performance Competency Secondary Skill Set", secondarySkills)}
          </MDBox>
        </Card>
      </MDBox>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => handleConfirmDialogClose(false)} maxWidth="sm" fullWidth PaperProps={{sx: {maxWidth: 480}}}>
        <DialogTitle sx={{pb: 0.5, pt: 1.5, px: 2, fontSize: "0.95rem", fontWeight: 600}}>
          Similar Skills Detected
        </DialogTitle>
        <DialogContent sx={{pt: 0.75, pb: 1, px: 2}}>
          {confirmDialog.duplicateInfo.map((item, index) => (
            <MDBox key={index} mb={1}>
              <MDTypography variant="body2" fontWeight="medium" sx={{mb: 0.35, fontSize: "0.82rem"}}>
                &quot;{item.newSkill}&quot; is similar to:
              </MDTypography>
              {item.matches.map((match, i) => (
                <MDBox key={i} ml={1.5} mb={0.2}>
                  <MDTypography variant="body2" sx={{fontSize: "0.8rem"}}>
                    • &quot;{match.existingSkill}&quot; in <strong>{match.categoryDisplay}</strong>
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
          ))}
          <MDTypography variant="body2" mt={1} fontWeight="medium" sx={{fontSize: "0.82rem"}}>
            Would you like to add these skills anyway?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{px: 2, pb: 1.5, pt: 0.5, gap: 1}}>
          <MDButton variant="outlined" color="dark" onClick={() => handleConfirmDialogClose(false)} size="small">No, Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={() => handleConfirmDialogClose(true)} size="small">Yes, Add Skills</MDButton>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      {notification.open && (
        <MDBox sx={{position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: "280px", maxWidth: "460px"}}>
          <Alert
            severity={notification.type}
            onClose={() => setNotification({open: false, message: "", type: ""})}
            sx={{whiteSpace: "pre-line", fontSize: "0.8rem", "& .MuiAlert-message": {width: "100%"}}}
          >
            {notification.message}
          </Alert>
        </MDBox>
      )}
    </DashboardLayout>
  );
}

export default AddTechnicalSkills;